<template>
  <transition name="fade">
    <div class="uom-overlay" v-if="show" @mousedown.self="$emit('close')">
      <div class="uom-dialog" role="dialog" aria-modal="true" aria-labelledby="uomTitle">
        <button class="uom-close" type="button" aria-label="Close" @click="$emit('close')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
        </button>

        <div class="uom-body">
          <aside class="uom-side">
            <div>
              <h2 id="uomTitle">Upgrade to the New Visceral Consignment</h2>
              <p class="uom-lede">Your shop keeps its data, its history, and its pricing. What changes is what you're able to do with it.</p>
            </div>

            <div class="uom-price">
              <span class="uom-plabel">Your exclusive pricing</span>
              <div class="uom-amt">{{ price }}<span>/mo</span></div>
              <div class="uom-reg">reg. <s>{{ regularPrice }}/mo</s><span v-if="discountPercent" class="uom-save">Save {{ discountPercent }}%</span></div>
              <p class="uom-pnote">Your exclusive pricing is reserved — every feature listed is included at that rate.</p>
            </div>

            <div class="uom-help">
              <span class="uom-avatar" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M4 14v-2a8 8 0 0 1 16 0v2"/>
                  <path d="M4 14.5a2 2 0 0 1 2-2h1v5.5H6a2 2 0 0 1-2-2z"/>
                  <path d="M20 14.5a2 2 0 0 0-2-2h-1V18h1a2 2 0 0 0 2-2z"/>
                  <path d="M19 18v.6a2.4 2.4 0 0 1-2.4 2.4H13"/>
                </svg>
              </span>
              <div>
                <p class="uom-q">Questions?</p>
                <p>We're here to help.</p>
                <div class="uom-links">
                  <a :href="demoUrl" target="_blank" rel="noopener">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2.5"/><path d="M3 10.5h18M8 3v4M16 3v4"/></svg>
                    Schedule a demo
                  </a>
                  <a :href="supportMailto" title="support@visceralapps.zohodesk.com">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="m3.5 7 8.5 6 8.5-6"/></svg>
                    Email us about your upgrade
                  </a>
                  <a href="#" @click.prevent="$emit('watch-video', 'whyMove')">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="m10 8.5 5.5 3.5-5.5 3.5z"/></svg>
                    Watch why shops are moving (1:40)
                  </a>
                </div>
              </div>
            </div>
          </aside>

          <div class="uom-main">
            <section>
              <h3 class="uom-h">What you get on the new app</h3>
              <p class="uom-sub">Highlights from the new app.</p>
              <div class="uom-why">
                <div class="uom-card">
                  <div class="uom-chead">
                    <span class="uom-disc" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m4 7.5 8-4 8 4v9l-8 4-8-4z"/><path d="m4 7.5 8 4 8-4M12 11.5v9"/></svg>
                    </span>
                    <p>Consignors add their own products</p>
                  </div>
                  <p class="uom-cbody">They submit from their portal; nothing goes live until you approve it.</p>
                </div>
                <div class="uom-card">
                  <div class="uom-chead">
                    <span class="uom-disc" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17z"/><path d="m14 6.5 3.5 3.5"/></svg>
                    </span>
                    <p>Edit requests in one place</p>
                  </div>
                  <p class="uom-cbody">Consignors request stock, price and product changes; you approve or decline.</p>
                </div>
                <div class="uom-card">
                  <div class="uom-chead">
                    <span class="uom-disc" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h9M19 7h1M4 17h5M15 17h5"/><circle cx="16" cy="7" r="2.4"/><circle cx="12" cy="17" r="2.4"/></svg>
                    </span>
                    <p>Payout rules that fit how you pay</p>
                  </div>
                  <p class="uom-cbody">Tiered rates, minimum payouts and per-product overrides, saved as reusable profiles.</p>
                </div>
                <div class="uom-card">
                  <div class="uom-chead">
                    <span class="uom-disc" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 19 15.5 8.5"/><path d="m17.5 3.5.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z"/><path d="m7 4 .6 1.6L9.2 6.2 7.6 6.8 7 8.4 6.4 6.8 4.8 6.2 6.4 5.6z"/></svg>
                    </span>
                    <p>Build payout rules by describing them</p>
                  </div>
                  <p class="uom-cbody">Explain your commission structure in plain English and the rules are built for you.</p>
                </div>
                <div class="uom-card">
                  <div class="uom-chead">
                    <span class="uom-disc" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/><path d="m9 15 2 2 4-4"/></svg>
                    </span>
                    <p>Contracts signed in the portal</p>
                  </div>
                  <p class="uom-cbody">Upload agreements for consignors to sign, and share files with each one.</p>
                </div>
                <div class="uom-card">
                  <div class="uom-chead">
                    <span class="uom-disc" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M6 3h12v18l-3-2-3 2-3-2-3 2z"/><path d="M9.5 8.5h5M9.5 12.5h5"/></svg>
                    </span>
                    <p>Every payout, itemized</p>
                  </div>
                  <p class="uom-cbody">Click any line item to see exactly how its payout was calculated.</p>
                </div>
              </div>

              <div class="uom-plus">
                <h3 class="uom-h">Plus</h3>
                <div class="uom-pills">
                  <span class="uom-pill" v-for="item in plusFeatures" :key="item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
                    {{ item }}
                  </span>
                </div>
              </div>
            </section>

            <section>
              <h3 class="uom-h">How migration works</h3>
              <p class="uom-sub">We do the work. You switch when you're ready.</p>
              <ol class="uom-steps">
                <li class="uom-step" v-for="step in migrationSteps" :key="step.number">
                  <span class="uom-num">{{ step.number }}</span>
                  <p>{{ step.text }}</p>
                </li>
              </ol>
            </section>

            <section>
              <h3 class="uom-h uom-h--solo">What moves with you</h3>
              <div class="uom-moves">
                <span class="uom-move" v-for="item in movesWithYou" :key="item">
                  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="m7.6 12.2 2.9 2.9 5.9-6" fill="none" stroke="#fff" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  {{ item }}
                </span>
              </div>
            </section>
          </div>
        </div>

        <footer class="uom-foot">
          <div class="uom-agree">
            <label class="uom-check" :class="{ 'is-flagged': showNudge }" for="uomAgree">
              <input id="uomAgree" type="checkbox" v-model="confirmationChecked">
              I understand and agree to proceed
            </label>
            <p class="uom-nudge" role="status" v-if="showNudge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7.5v5M12 16.2v.1"/></svg>
              Please confirm you'd like to proceed.
            </p>
            <p class="uom-fine">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="10.5" width="16" height="10" rx="2"/><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5"/></svg>
              No commitment. Cancel anytime.
            </p>
          </div>

          <button
            class="uom-cta"
            type="button"
            :aria-disabled="String(!confirmationChecked || submitting)"
            @click="upgrade">
            {{ submitting ? 'Starting…' : 'Start my upgrade' }}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6"/></svg>
          </button>
        </footer>

        <p class="uom-error" v-if="error">{{ error }}</p>
      </div>
    </div>
  </transition>
</template>

<script>
import client from '@/mixins/client'

export default {
  name: 'UpgradeOfferModal',
  mixins: [client],
  props: {
    show: {
      type: Boolean,
      default: false
    },
    shop: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      confirmationChecked: false,
      showNudge: false,
      submitting: false,
      error: null,
      price: '$39.99',
      regularPrice: '$59.99',
      supportMailto: 'mailto:support@visceralapps.zohodesk.com?subject=Question%20about%20my%20upgrade',
      // Same Calendly the tutorial steps link to; swap in a dedicated event if one exists.
      demoUrl: 'https://calendly.com/support-visceral/onboarding',
      migrationSteps: [
        { number: 1, text: 'You accept your offer' },
        { number: 2, text: 'We prepare your shop' },
        { number: 3, text: 'We migrate everything' },
        { number: 4, text: 'We verify your data' },
        { number: 5, text: 'You switch when ready' }
      ],
      plusFeatures: [
        'Custom portal domain with SSL',
        'Recurring consignor fees',
        'AI help assistant',
        'Variant-level consignor tracking',
        'Bulk consignor CSV import',
        'Editable exports & email templates'
      ],
      movesWithYou: [
        'Products & inventory',
        'Settings & preferences',
        'Consignors & contracts',
        'Payout rules & balances',
        'Sales history',
        'Reports & statements'
      ]
    }
  },
  computed: {
    // Derived from the two prices above so the badge can never drift from them.
    discountPercent () {
      const now = parseFloat(String(this.price).replace(/[^0-9.]/g, ''))
      const reg = parseFloat(String(this.regularPrice).replace(/[^0-9.]/g, ''))
      if (!now || !reg || reg <= now) return 0
      return Math.round((1 - now / reg) * 100)
    }
  },
  watch: {
    show (isOpen) {
      // Reset the gate each time the modal opens so a previous visit can't leave it armed.
      if (isOpen) {
        this.confirmationChecked = false
        this.showNudge = false
        this.error = null
      }
      document.body.classList.toggle('modal-open', isOpen)
    },
    confirmationChecked (checked) {
      if (checked) this.showNudge = false
    }
  },
  methods: {
    upgrade () {
      // The button stays visually present but inert until the box is ticked — clicking it
      // early points the merchant at the checkbox rather than doing nothing.
      if (!this.confirmationChecked) {
        this.showNudge = true
        return
      }
      if (this.submitting) return

      const shopDomain = this.shop && this.shop.myshopify_domain ? this.shop.myshopify_domain : ''

      this.submitting = true
      this.error = null

      this.request('patch', '/shopify/shops/confirm-upgrade/', {
        shop: shopDomain
      }).then(response => {
        this.submitting = false
        this.$store.state.shop.agree_to_upgrade = true
        this.$store.state.shop.agree_to_upgrade_datetime = new Date().toISOString()
        this.$emit('close')
      }).catch(error => {
        this.submitting = false
        this.error = 'We couldn\'t start your upgrade just now. Please try again, or email us and we\'ll take care of it.'
        console.error('Upgrade request failed:', error)
      })
    },
    onKeydown (event) {
      if (this.show && event.key === 'Escape') this.$emit('close')
    }
  },
  created () {
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeydown)
    document.body.classList.remove('modal-open')
  }
}
</script>

<!-- Unscoped: the scroll lock has to land on <body>, which no scoped selector can reach. -->
<style lang="scss">
body.modal-open {
  overflow: hidden;
}
</style>

<style lang="scss" scoped>
// Self-contained palette, matching UpgradeBanner. The modal must look identical whether the
// dashboard is running the dark or the `light-theme` styling.
.uom-overlay {
  --uom-ink: #2a1a63;
  --uom-ink-soft: #514b6b;
  --uom-violet: #6b32d6;
  --uom-violet-tint: #f3eafd;
  --uom-gold: #f5b32c;
  --uom-gold-hi: #ffc348;
  --uom-line: #e9e5f2;
  --uom-muted: #9a95ad;

  position: fixed;
  inset: 0;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1000;
  background: rgba(30, 16, 72, .55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 18px;
  font-family: Roboto, Helvetica, sans-serif;
  color: var(--uom-ink-soft);
  -webkit-font-smoothing: antialiased;
}

.uom-dialog {
  width: 100%;
  max-width: 1020px;
  max-height: 90vh;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 40px 80px -30px rgba(24, 12, 60, .55);
}

.uom-close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 3;
  width: 32px;
  height: 32px;
  min-width: 32px;
  border: 0;
  // The app's global `button` rule sets padding:12px 18px; with border-box that leaves a
  // 32px button no room for its icon at all. Reset it here rather than inherit the default.
  padding: 0;
  border-radius: 8px;
  cursor: pointer;
  background: rgba(255, 255, 255, .9);
  color: #5b5477;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(24, 12, 60, .16);
  transition: color .15s, background .15s;

  &:hover {
    background: #fff;
    color: var(--uom-ink);
  }

  svg {
    width: 16px;
    height: 16px;
  }
}

.uom-body {
  display: flex;
  flex-direction: column;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  position: relative;

  > * {
    flex: none;
  }
}

/* ---------- left panel ---------- */
.uom-side {
  background: linear-gradient(160deg, #7b3fe4 0%, #5a22c4 52%, #43159e 100%);
  color: #fff;
  padding: 28px 26px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    width: 280px;
    height: 280px;
    right: -120px;
    top: -120px;
    background: radial-gradient(circle, rgba(255, 255, 255, .16), transparent 65%);
  }

  > * {
    position: relative;
    z-index: 1;
  }

  h2 {
    font-size: 22px;
    font-weight: 900;
    line-height: 1.18;
    letter-spacing: -.02em; // the global heading rule adds +1px tracking
    color: #fff;
    margin: 0;
    max-width: 17ch;
  }
}

.uom-lede {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: rgba(255, 255, 255, .82);
  max-width: 32ch;
}

.uom-price {
  background: #fff;
  border-radius: 12px;
  padding: 16px 18px;
  color: var(--uom-ink-soft);
}

.uom-plabel {
  display: block;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--uom-violet);
  margin-bottom: 8px;
}

.uom-amt {
  font-size: 32px;
  font-weight: 900;
  letter-spacing: -.025em;
  line-height: 1;
  color: var(--uom-violet);

  span {
    font-size: 15px;
  }
}

.uom-save {
  display: inline-block;
  margin-left: 9px;
  padding: 4px 8px;
  border-radius: 999px;
  background: var(--uom-violet-tint);
  color: var(--uom-violet);
  font-size: 10.5px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: .06em;
  text-transform: uppercase;
  vertical-align: middle;
  white-space: nowrap;
}

.uom-reg {
  font-size: 12px;
  color: var(--uom-muted);
  margin-top: 5px;
}

.uom-pnote {
  margin: 12px 0 0;
  padding-top: 12px;
  border-top: 1px solid var(--uom-line);
  font-size: 12.5px;
  line-height: 1.45;
  color: var(--uom-ink-soft);
}

.uom-help {
  margin-top: auto;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, .2);

  p {
    margin: 0;
    font-size: 12.5px;
    color: rgba(255, 255, 255, .8);
  }

  a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    color: #fff;
    font-size: 12.5px;
    font-weight: 500;
    line-height: 1.35;
    text-decoration: none;
    border-bottom: 1px solid rgba(255, 255, 255, .45);
    padding-bottom: 2px;
    overflow-wrap: anywhere;

    &:hover,
    &:focus {
      color: #fff;
      border-color: #fff;
    }

    svg {
      width: 13px;
      height: 13px;
      flex: none;
    }
  }
}

// The side panel is too narrow to seat both links on one line, so they stack as rows.
// Qualified with .uom-help so it outranks the `.uom-help a` margin above regardless of
// the order cssnano emits them in.
.uom-links {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 9px;
  margin-top: 10px;
}

.uom-help .uom-links a {
  margin-top: 0;
}

.uom-help .uom-q {
  font-size: 13.5px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 2px;
}

.uom-avatar {
  flex: none;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: rgba(255, 255, 255, .16);
  border: 1px solid rgba(255, 255, 255, .3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;

  svg {
    width: 21px;
    height: 21px;
  }
}

/* ---------- right panel ---------- */
.uom-main {
  padding: 26px 24px;
  display: flex;
  flex-direction: column;
  gap: 26px;
  min-width: 0;
}

.uom-h {
  font-size: 15px;
  font-weight: 900;
  letter-spacing: -.01em;
  color: var(--uom-ink);
  margin: 0 0 4px;
}

// Two classes so it beats the rule above whatever order cssnano emits: sections without a
// sub-line need the spacing the sub would otherwise provide.
.uom-h.uom-h--solo {
  margin-bottom: 12px;
}

.uom-sub {
  margin: 0 0 14px;
  font-size: 12.5px;
  line-height: 1.4;
  color: var(--uom-muted);
}

.uom-why {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

.uom-card {
  border: 1px solid var(--uom-line);
  border-radius: 11px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 7px;

  p {
    margin: 0;
    font-size: 12.5px;
    line-height: 1.35;
    color: var(--uom-ink);
    font-weight: 700;
  }

  // Two classes deep so it outranks the title rule above no matter how cssnano orders them.
  .uom-cbody {
    margin-top: 4px;
    font-size: 11.5px;
    font-weight: 400;
    line-height: 1.4;
    color: var(--uom-ink-soft);
  }
}

// The runners-up: named individually so the depth is visible, but pill-sized so six of
// them cost about the same as the one prose line they replace.
.uom-plus {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--uom-line);
}

.uom-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 10px;
}

.uom-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 11px;
  border: 1px solid var(--uom-line);
  border-radius: 999px;
  background: #faf9fc;
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1;
  color: var(--uom-ink-soft);

  svg {
    flex: none;
    width: 11px;
    height: 11px;
    color: var(--uom-violet);
  }
}

// Icon and title share a row, with the supporting line below at the card's full width:
// stacking the icon above the title cost ~42px per grid row and bought nothing.
.uom-chead {
  display: flex;
  align-items: center;
  gap: 9px;
}

.uom-disc {
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--uom-violet-tint);
  color: var(--uom-violet);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
  }
}

.uom-steps {
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
  list-style: none;
  margin: 0;
  padding: 0;
}

.uom-step {
  display: flex;
  gap: 12px;
  align-items: center;

  p {
    margin: 0;
    font-size: 12.5px;
    line-height: 1.35;
    color: var(--uom-ink-soft);
  }
}

.uom-num {
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--uom-violet-tint);
  color: var(--uom-violet);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 900;
  line-height: 1;
  border: 1px solid #e3d5fa;
}

// A plain three-column list rather than pills: same six items, ~25px shorter, because the
// chips wrapped to two rows and paid border and padding on every one of them.
.uom-moves {
  display: grid;
  grid-template-columns: 1fr;
  gap: 9px 18px;
}

.uom-move {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 12.5px;
  line-height: 1.3;
  color: var(--uom-ink-soft);

  svg {
    flex: none;
    width: 16px;
    height: 16px;
    color: #1fa971;
  }
}

/* ---------- footer ---------- */
.uom-foot {
  flex: none;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  padding: 14px 20px;
  background: #faf9fc;
  border-top: 1px solid var(--uom-line);
  flex-direction: column;
  align-items: stretch;
}

.uom-agree {
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
}

.uom-check {
  display: flex;
  align-items: center;
  gap: 9px;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.35;
  letter-spacing: 0;
  color: var(--uom-ink);
  padding: 6px 9px;
  margin: -6px -9px;
  border-radius: 8px;
  border: 1px solid transparent;
  transition: background .2s, border-color .2s;

  &.is-flagged {
    background: #fff6e4;
    border-color: #efcc8a;
  }

  input {
    flex: none;
    width: 17px;
    height: 17px;
    margin: 0;
    cursor: pointer;
    accent-color: var(--uom-violet);
  }
}

.uom-nudge {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.35;
  color: #9a6000;

  svg {
    flex: none;
    width: 14px;
    height: 14px;
  }
}

.uom-fine {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  font-size: 11.5px;
  color: var(--uom-muted);

  svg {
    width: 13px;
    height: 13px;
    color: #a79fc4;
  }
}

.uom-cta {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: var(--uom-gold);
  color: #241543;
  border: 0;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: .045em;
  text-transform: uppercase;
  padding: 14px 18px;
  border-radius: 9px;
  cursor: pointer;
  box-shadow: 0 1px 0 rgba(0, 0, 0, .06);
  transition: background .15s, transform .15s, box-shadow .15s;

  svg {
    width: 15px;
    height: 15px;
    transition: transform .18s;
  }

  &:hover {
    background: var(--uom-gold-hi);
    box-shadow: 0 6px 16px -8px rgba(180, 120, 0, .65);
  }

  &:hover svg {
    transform: translateX(3px);
  }

  &:active {
    transform: translateY(1px);
  }

  // Inert-but-present: still clickable so the click can point at the checkbox.
  &[aria-disabled="true"] {
    background: #eae6f2;
    color: #a79fc4;
    box-shadow: none;

    &:hover {
      background: #e3deee;
      box-shadow: none;
    }

    &:hover svg {
      transform: none;
    }
  }
}

.uom-error {
  flex: none;
  margin: 0;
  padding: 10px 20px;
  background: #fdecec;
  border-top: 1px solid #f3c9c9;
  color: #9a2020;
  font-size: 12.5px;
  line-height: 1.4;
}

.uom-close:focus-visible,
.uom-cta:focus-visible,
.uom-check input:focus-visible {
  outline: 2px solid var(--uom-violet);
  outline-offset: 2px;
}

.uom-help a:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 3px;
}

@media (min-width: 760px) {
  .uom-body {
    display: grid;
    grid-template-columns: minmax(0, 320px) minmax(0, 1fr);
    overflow: hidden;
    min-height: 0;

    > * {
      min-height: 0;
    }
  }

  .uom-side {
    padding: 34px 30px;
    overflow: auto;

    h2 {
      font-size: 26px;
    }
  }

  .uom-main {
    padding: 34px 32px;
    overflow: auto;
  }

  .uom-h {
    font-size: 16px;
  }

  .uom-why {
    grid-template-columns: repeat(3, 1fr);
  }

  .uom-moves {
    grid-template-columns: repeat(3, 1fr);
  }

  .uom-steps {
    flex-direction: row;
    gap: 8px;

    // Dotted connector running behind the numbered discs.
    &::before {
      content: "";
      position: absolute;
      top: 17px;
      left: 34px;
      right: 34px;
      height: 1px;
      background: repeating-linear-gradient(90deg, #dfd6f4 0 5px, transparent 5px 10px);
    }
  }

  .uom-step {
    flex: 1;
    flex-direction: column;
    text-align: center;
    gap: 9px;
    position: relative;
    z-index: 1;

    p {
      font-size: 11.5px;
    }
  }

  .uom-num {
    box-shadow: 0 0 0 5px #fff;
  }

  .uom-foot {
    padding: 16px 30px;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

@media (prefers-reduced-motion: reduce) {
  .uom-cta,
  .uom-cta svg,
  .uom-check,
  .uom-close {
    transition: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/apps/vendorconsignment/components/UpgradeOfferModal.vue